import type { GeometryElement } from 'jsxgraph';
import type { ElementDisplayConfig, FeatureElementDef, FeatureType } from '../../../graphpad.model';
import type { CreatedElement, ElementContext } from '../../element.model';
import { BaseFeatureCreator } from '../feature.model';
import { resolveFeatureStyle, resolveFeatureVisibility } from '../feature-style.model';
import type { YInterceptParams } from './y-intercept.model';

/**
 * Creates a y-intercept point for any function type.
 * The y-intercept is the point where the function crosses the y-axis (x = 0).
 */
export class YInterceptCreator extends BaseFeatureCreator {
  readonly featureType: FeatureType = 'yIntercept';
  override readonly computedValueKeys = ['x', 'y'] as const;

  create(
    id: string,
    def: FeatureElementDef,
    display: ElementDisplayConfig | undefined,
    context: ElementContext,
  ): CreatedElement {
    const { board, elementFactory, featureTheme } = context;
    const params = def.params as YInterceptParams | undefined;
    if (!params?.of) {
      throw new Error('Y-intercept feature requires params.of to specify the parent function');
    }
    const parentFn = this.getEvaluableParent(params.of, context);
    const objects: GeometryElement[] = [];

    const style = resolveFeatureStyle('point', this.featureType, context, display, []);
    const visible = resolveFeatureVisibility(this.featureType, featureTheme, display, []);

    const result = elementFactory.point({
      coords: [
        0,
        () => {
          const y = parentFn.evaluate(0);
          return isFinite(y) ? y : NaN;
        },
      ],
      name: 'y-int',
      style: { ...style, visible },
    });
    objects.push(result.point);
    if (result.label) {
      objects.push(result.label);
    }

    return {
      id,
      type: 'yIntercept',
      objects,
      getComputedValues: () => ({
        x: () => 0,
        y: () => {
          const yVal = parentFn.evaluate(0);
          return isFinite(yVal) ? yVal : NaN;
        },
      }),
      destroy: () => objects.forEach((obj) => board.removeObject(obj)),
    };
  }
}
